import { getBaseNodeTypeName } from '@atlaskit/editor-common/utils/node-type-utils';
import type { NodeSpec } from '@atlaskit/editor-prosemirror/model';
import { TextSelection } from '@atlaskit/editor-prosemirror/state';
import { fg } from '@atlaskit/platform-feature-flags/fg';

import type {
	StreamingCommandFactory,
	SetAttrsCommand,
} from '../aiStreamingOrchestratorPluginType';
import { findNodeByLocalId } from './utils/find-node-by-local-id';

/**
 * Splits incoming attrs into those the node's spec recognizes and those it
 * doesn't. Unknown keys (e.g. a `url` attr on a `panel`) are reported instead
 * of being silently discarded, so callers can surface/telemeter them.
 */
const getValidAttributes = (attrs: Record<string, unknown>, nodeSpec: NodeSpec) => {
	const validAttrs: Record<string, unknown> = {};
	const unknownKeys: string[] = [];
	for (const key in attrs) {
		if (nodeSpec.attrs?.hasOwnProperty(key)) {
			validAttrs[key] = attrs[key];
		} else {
			unknownKeys.push(key);
		}
	}
	return { validAttrs, unknownKeys };
};

export const createSetAttrsCommand: StreamingCommandFactory<SetAttrsCommand> =
	(api) => (command) => {
		return api.core.actions.execute(({ tr }) => {
			let shouldExecute = false;
			findNodeByLocalId(api, tr, command.localId, ({ node, pos }) => {
				if (node) {
					const attrs = command.attrs;
					if (!attrs || typeof attrs !== 'object') {
						return;
					}

					const { validAttrs, unknownKeys } = getValidAttributes(attrs, node.type.spec);

					if (unknownKeys.length > 0 && fg('platform_editor_ai_invalid_attrs_telemetry')) {
						// Telemetry only for now — still silently dropping
						// unknown keys rather than repairing them. Layering
						// validateAndRepairNodeAttrs onto this path (behind its
						// own separate experiment) is deferred to a follow-up
						// PR so the data this produces can inform whether/how
						// that repair should work, instead of shipping both
						// at once.
						api.aiStreamingOrchestrator?.actions.notifyInvalidAttrs(
							getBaseNodeTypeName(node.type),
							// Key name only, never the value — attribute values can carry UGC.
							unknownKeys[0],
						);
					}

					api.aiExperience?.actions.tagAsAIStreamingTransformation(tr, { isFinal: true });

					tr.setNodeMarkup(pos, node.type, {
						...node.attrs,
						...validAttrs,
					});
					tr.setSelection(TextSelection.near(tr.doc.resolve(pos)));

					shouldExecute = true;
				}
			});
			if (shouldExecute) {
				api.aiExperience?.actions.stampAgentEditChrome(tr);
			}
			return shouldExecute ? tr : null;
		});
	};
